import { order, type product as typeProduct } from '@cashew/types';
/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { useEffect, useMemo, useState } from 'react';
import { Helmet } from 'react-helmet';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useParams } from 'react-router-dom';
import {
  BackArrow,
  Box,
  Button,
  ModalConfirm,
  PageWithNavigation,
  Title,
} from '../../components';
import Content from './Content';
import DispatchModal from './DispatchModal';
import RefundModal from './RefundModal';
import './index.css';
import Timeline from '../../components/Timeline';
import { initialOrder } from '../../helpers/initialorder';
import { Divider } from '../../library';
import ChargebackModal from './Chargeback';
import ChargebackList from './Chargeback/ChargebackList';
import type { ChargebackData } from './Chargeback/ChargebackList/index.types';
import { getInstallmentDetails, useGetOrderDetails } from './api';
import useOrderDetailsTimeline from './hooks/useOrderDetailsTimeline';
import css from './index.module.scss';

const { api, dateFormat, priceFormat } = require('@cashew/utils');

const ns = 'order-details';
const KYCRejReason = 'ID_VENDOR_REJECTION';
const textFormatter = (text: string): string =>
  text[0] + text.toLocaleLowerCase().replaceAll('_', ' ').slice(1);

const getOrdinal = ({ number }: { number: number }) =>
  `${number}${['st', 'nd', 'rd'][((((number + 90) % 100) - 10) % 10) - 1] || 'th'}`;

/**
 * Orders Page RefundPage
 */
const OrderDetail = () => {
  const { t } = useTranslation();
  const params = useParams<{ orderId: string; lang: string }>();
  const dispatch = useDispatch();

  const { token, role } = useSelector((state: any) => state.user);

  const [orderDetails, setOrderDetails] = useState<order.OrderType | any>(
    initialOrder
  );
  const [loading, setLoading] = useState(false);
  const [isOffline, setIsOffline] = useState(false);
  const [dispatchUpdateModal, setDispatchUpdateModal] = useState(false);
  const [refundModal, setRefundModal] = useState(false);
  const [isChargebackModalOpen, setIsChargebackModalOpen] = useState(false);
  const [purchaseFeeRefundModal, setPurchaseFeeRefundModal] = useState(false);

  const [eligibleDispatch, setEligibleDispatch] = useState(false);
  const [ischargebackEligible, setIsChargebackEligible] = useState(false);
  const [chargebacks, setChargebacks] = useState<ChargebackData[]>([]);
  const [isRefundEligible, setIsRefundEligible] = useState(false);

  const [paidAmount, setPaidAmount] = useState(0);
  const [nextPayment, setNextPayment] = useState({
    installmentAmount: 0,
    latePaymentAmount: 0,
    latePaymentAmountVat: 0,
    chargeDate: '',
  });
  const [product, setProduct] = useState<typeProduct.ProductType | any>(
    undefined
  );

  const history = useHistory();
  const { type }: any = useParams();

  const {
    data: timelineDetails = [],
    loading: isTimelineLoading,
    getOrderDetailsTimeline,
  } = useOrderDetailsTimeline(params?.orderId);

  const checkChargebackEligibility = () => {
    api
      .get({
        endpoint: `payments/chargeback/${params.orderId}/eligibility`,
        headers: {
          Authorization: token,
        },
      })
      .then((resp: any) => {
        setIsChargebackEligible(resp?.data?.isPaymentChargebackEligible);
      })
      .catch(() => {
        setIsChargebackEligible(false);
      });
  };

  const getChargebacks = ({ orderId }: { orderId: string }) => {
    setChargebacks([]);
    if (orderId) {
      api
        .get({
          endpoint: `payments/chargeback/search?orderId=${orderId}`,
        })
        .then((resp: any) => {
          const list =
            resp?.data?.map((chargebackItem: ChargebackData) => {
              const installments = chargebackItem.installments.reduce(
                (acc: any, obj: any) => {
                  const { maxInstallmentNumber, minInstallmentNumber } = acc;
                  const { installmentNumber } = obj;

                  return {
                    maxInstallmentNumber: Math.max(
                      maxInstallmentNumber,
                      installmentNumber
                    ),
                    minInstallmentNumber: Math.min(
                      minInstallmentNumber,
                      installmentNumber
                    ),
                  };
                },
                {
                  maxInstallmentNumber: Number.NEGATIVE_INFINITY,
                  minInstallmentNumber: Number.POSITIVE_INFINITY,
                }
              );

              const processedInstallment =
                installments.minInstallmentNumber ===
                installments.maxInstallmentNumber
                  ? getOrdinal({ number: installments.minInstallmentNumber })
                  : `${getOrdinal({ number: installments.minInstallmentNumber })} to ${getOrdinal({ number: installments.maxInstallmentNumber })}`;
              return { ...chargebackItem, processedInstallment };
            }) || [];
          setChargebacks(list);
        })
        .catch(() => {});
    }
  };

  const getEligibleDispatchRefund = () => {
    api
      .get({
        endpoint: `orders/${params.orderId}/dispatch/eligible`,
        headers: {
          Authorization: token,
        },
      })
      .then((resp: any) => {
        setEligibleDispatch(resp.status === 'success');
      })
      .catch(() => {
        setEligibleDispatch(false);
      });
  };

  useEffect(() => {
    if (params.orderId) {
      getInstallmentDetails(
        params.orderId,
        setProduct,
        setPaidAmount,
        setNextPayment,
        history
      );
    }
  }, [params.orderId]);

  useEffect(() => {
    if (orderDetails?.productType === 'SUBSCRIPTION') {
      history.push(`/${params.lang}/subscriptions/${params.orderId}`);
    }
  }, [orderDetails]);

  useEffect(() => {
    if (
      orderDetails?.store?.type &&
      orderDetails?.store?.type === 'sharaf_dg'
    ) {
      setIsOffline(true);
    }
  }, [orderDetails]);

  useEffect(() => {
    if (orderDetails.id === 0 || !loading) {
      // eslint-disable-next-line react-hooks/rules-of-hooks
      useGetOrderDetails(
        params.orderId,
        setOrderDetails,
        setLoading,
        dispatch,
        t
      );
    }
    checkChargebackEligibility();
    getChargebacks({ orderId: params?.orderId });
    getEligibleDispatchRefund();
  }, [orderDetails, refundModal]);

  useEffect(() => {
    try {
      if (orderDetails && orderDetails.id) {
        api
          .get({
            endpoint: `refunds/divide/${orderDetails?.id}/eligibility`,
          })
          .then((resp: any) => {
            console.log(resp.data.refundEligible);
            setIsRefundEligible(resp.data.refundEligible);
          })
          .catch(() => {
            setIsRefundEligible(false);
          });
      }
    } catch (e: any) {
      console.error(e);
    }
  }, [orderDetails, type]);

  const eligibleDispute =
    orderDetails.orderStatus === order.OrderStatusEnum.AUTHORISED;

  const handleRefundPurchaseFee = () => {
    api
      .post({
        endpoint: 'refunds',
        params: {
          orderId: params?.orderId,
          refundAmount: orderDetails?.balanceAmount,
          cashewCustomerId: orderDetails?.cashewCustomerId,
          storeId: orderDetails?.storeId,
          purchaseFeeRefund: true,
        },
      })
      .then(() => {
        window.location.reload();
      })
      .catch((error: any) => {
        // eslint-disable-next-line no-console
        console.log(error);
        setPurchaseFeeRefundModal(false);
      })
      .finally(() => {
        setPurchaseFeeRefundModal(false);
        window.location.reload();
      });
  };

  const closeChargebackModal = () => {
    setIsChargebackModalOpen(false);
    checkChargebackEligibility();
    getChargebacks({ orderId: params?.orderId });
    getOrderDetailsTimeline();
  };

  const productType = useMemo(
    () =>
      orderDetails?.productType === 'SUBSCRIPTION' ? 'subscription' : 'order',
    [orderDetails?.productType]
  );

  const instalments = product?.subProducts?.[0]?.installments || [];

  const customFields = orderDetails?.customFields || {};
  const customFieldsKeys = Object.keys(customFields);
  return (
    <div className={ns}>
      <Helmet>
        <title>{t('orderDetails.sectionTitle')}</title>
      </Helmet>
      <PageWithNavigation>
        {orderDetails?.productType !== 'SUBSCRIPTION' && (
          <>
            <Title>
              Order {orderDetails.id}
              <BackArrow title={t(`orderDetails.button.back.${type}`)} />
            </Title>
            <Box withHeader className={`${ns}__box`}>
              <div className={`${ns}__line`}>
                <p>
                  <span>{t('orderDetails.orderStatus')}</span>
                  <strong>{t(`status.${orderDetails.orderStatus}`)}</strong>
                </p>
                <p>
                  <span>Total order amount</span>
                  <strong>
                    {priceFormat({
                      currency: orderDetails.currencyCode,
                      amount: orderDetails.totalAmount || 0,
                    })}
                  </strong>
                </p>
                <p>
                  <span>Dispatch status</span>
                  <strong>
                    {t(`status.${orderDetails.shipping?.status}`)}
                  </strong>
                </p>
                <p>
                  <span>Refund status</span>
                  <strong>
                    {t(
                      `status.${
                        orderDetails.refundStatus
                          ? orderDetails.refundStatus
                          : 'NOT_REFUNDED'
                      }`
                    )}
                  </strong>
                </p>
                <p>
                  <span>
                    {t(
                      isOffline
                        ? 'orderDetails.merchantOrderCode'
                        : 'orderDetails.orderReference'
                    )}
                  </span>
                  <strong>{orderDetails.orderReference}</strong>
                </p>
                <p>
                  <span>{t('orderDetails.totalRefundAmount')}</span>
                  <strong>
                    {orderDetails?.purchaseFeeStatus === 'REFUNDED'
                      ? `${priceFormat({
                          currency: orderDetails.currencyCode,
                          amount:
                            Number(
                              (
                                (orderDetails.totalAmount || 0) -
                                (orderDetails.balanceAmount || 0)
                              ).toFixed(2)
                            ) +
                            Number(orderDetails?.sumPurchaseFeePurchaseFeeVat),
                        })} (Purchase fee included)`
                      : priceFormat({
                          currency: orderDetails.currencyCode,
                          amount: Number(
                            (
                              (orderDetails.totalAmount || 0) -
                              (orderDetails.balanceAmount || 0)
                            ).toFixed(2)
                          ),
                        })}
                  </strong>
                </p>
                <p>
                  <span>{t('orderDetails.storeName')}</span>
                  <strong>{orderDetails.store?.storeName}</strong>
                </p>

                <p className="order-details-paragrph">
                  <span>{t('orderDetails.customerName')}</span>
                  <strong>
                    {`${orderDetails.customer?.firstName || ''} ${orderDetails.customer?.lastName || ''}`}
                  </strong>
                </p>
                <p>
                  <span>{t('orderDetails.storeId')}</span>
                  <strong>{orderDetails.storeId}</strong>
                </p>

                <p className="order-details-paragrph">
                  <span>Customer Email</span>
                  <strong>{orderDetails.customer?.email}</strong>
                </p>
                <p>
                  <span>{t('orderDetails.orderDate')}</span>
                  <strong>
                    {orderDetails.id > 0
                      ? dateFormat({ date: orderDetails.purchaseDate })
                      : ''}
                  </strong>
                </p>
                <p>
                  <span>Customer phone</span>
                  <strong>{orderDetails.customer?.mobileNumber}</strong>
                </p>
                <p>
                  <span>Rejection reason</span>
                  <strong>
                    {orderDetails?.rejectionReason
                      ? textFormatter(orderDetails?.rejectionReason)
                      : ''}
                  </strong>
                </p>
                <p>
                  <span>KYC rejections reason</span>
                  <strong>
                    {orderDetails?.rejectionReason === KYCRejReason
                      ? orderDetails?.kycRejectionReason
                      : ''}
                  </strong>
                </p>

                <p>
                  <span>Dispatch date</span>
                  <strong>
                    {orderDetails?.shipping?.dispatchDate !== null &&
                      orderDetails?.shipping?.dispatchDate !== undefined &&
                      orderDetails?.shipping?.dispatchDate !== '' &&
                      dateFormat({
                        date: orderDetails?.shipping?.dispatchDate,
                        withTime: true,
                      })}
                  </strong>
                </p>


              {orderDetails && orderDetails?.sumPurchaseFeePurchaseFeeVat && (
                  <p>
                    <span>Purchase Fee</span>
                    <strong>
                      {/* eslint-disable-next-line no-mixed-operators */}
                      {(orderDetails &&
                        `${priceFormat({
                          currency: orderDetails?.currencyCode,
                          amount: orderDetails?.sumPurchaseFeePurchaseFeeVat,
                          // eslint-disable-next-line no-mixed-operators
                        })}  (VAT included)`) ||
                        0}
                    </strong>
                  </p>
                )}


                {orderDetails?.additionalFees?.some((fee: any) => fee?.type === 'PROCESSING_FEE') && (
                  <p>
                  <span>Processing fee</span>
                  <strong>
                  {(() => {
                  const processingFee = orderDetails?.additionalFees?.find((fee: any) => fee?.type === 'PROCESSING_FEE');
                  return processingFee
                    ? `${priceFormat({
                    currency: orderDetails?.currencyCode,
                    amount: (processingFee?.feeValue || 0) + (processingFee?.feeVat || 0),
                    })} (VAT included)`
                    : 0;
                  })()}
                  </strong>
                  </p>
                )}
              </div>
              <>
                {(isRefundEligible ||
                  eligibleDispatch ||
                  eligibleDispute ||
                  (orderDetails.refundStatus === 'REFUNDED' &&
                    orderDetails?.purchaseFeeStatus === 'PAID')) && (
                  <footer>
                    <div className={`${ns}__actions`}>
                      {process.env.REACT_APP_LOG_CHARGEBACK_ENABLED ===
                        'true' &&
                        ischargebackEligible && (
                          <Button
                            extraClass={
                              role === 'CUSTOMER_CARE' ? 'disableBtn' : ''
                            }
                            disabled={role === 'CUSTOMER_CARE'}
                            onClick={() => setIsChargebackModalOpen(true)}
                          >
                            Log chargeback
                          </Button>
                        )}
                      {isRefundEligible && (
                        <Button
                          extraClass={
                            role === 'CUSTOMER_CARE' ? 'disableBtn' : ''
                          }
                          disabled={role === 'CUSTOMER_CARE'}
                          onClick={() => setRefundModal(true)}
                        >
                          {t('orderDetails.button.requestNewRefund')}
                        </Button>
                      )}
                      {eligibleDispatch && (
                        <Button
                          extraClass={
                            orderDetails.refundStatus === 'REFUNDED' ||
                            role === 'CUSTOMER_CARE'
                              ? 'disableBtn'
                              : ''
                          }
                          disabled={
                            orderDetails.refundStatus === 'REFUNDED' ||
                            role === 'CUSTOMER_CARE'
                          }
                          onClick={() => setDispatchUpdateModal(true)}
                        >
                          Mark order as dispatched
                        </Button>
                      )}
                      {eligibleDispute && (
                        <>
                          <Button
                            extraClass={`${ns}__confirm`}
                            onClick={() => {
                              api
                                .patch({
                                  endpoint: `orders/${params.orderId}/status`,
                                  params: {
                                    orderStatusEnum: 'CONFIRMED',
                                  },
                                })
                                .then(() => {
                                  dispatch({
                                    type: 'OPEN_TOASTER',
                                    payload: {
                                      toasterTitle: t(
                                        'orderDetails.confirmed.order.title'
                                      ),
                                      toasterMessage: t(
                                        'orderDetails.confirmed.order.message'
                                      ),
                                      toasterType: 'success',
                                      toasterVisible: true,
                                      toasterDismiss: () => {
                                        window.location.reload();
                                      },
                                    },
                                  });
                                });
                            }}
                          >
                            {t('button.confirm')}
                          </Button>
                          <Button
                            extraClass={`${ns}__reject`}
                            onClick={() => {
                              api
                                .patch({
                                  endpoint: `orders/${params.orderId}/status`,
                                  params: {
                                    orderStatusEnum:
                                      order.OrderStatusEnum.REJECTED,
                                  },
                                })
                                .then(() => {
                                  dispatch({
                                    type: 'OPEN_TOASTER',
                                    payload: {
                                      toasterTitle: t(
                                        'orderDetails.rejected.order.title'
                                      ),
                                      toasterMessage: t(
                                        'orderDetails.rejected.order.message'
                                      ),
                                      toasterType: 'success',
                                      toasterVisible: true,
                                      toasterDismiss: null,
                                    },
                                  });
                                });
                            }}
                          >
                            {t('button.reject')}
                          </Button>
                        </>
                      )}
                     
                    </div>
                  </footer>
                )}
              </>
              <DispatchModal
                orderDetails={orderDetails}
                showConfirmModal={dispatchUpdateModal}
                setShowConfirmModal={setDispatchUpdateModal}
              />
              {isChargebackModalOpen ? (
                <ChargebackModal
                  currency={orderDetails?.currencyCode}
                  isModalOpen={isChargebackModalOpen}
                  closeModal={closeChargebackModal}
                />
              ) : (
                <></>
              )}
              <RefundModal
                orderDetails={orderDetails}
                showConfirmModal={refundModal}
                setShowConfirmModal={setRefundModal}
                dispatched={orderDetails.shipping?.status === 'DISPATCHED'}
              />

              <ModalConfirm
                isVisible={purchaseFeeRefundModal}
                onClose={() => {
                  setPurchaseFeeRefundModal(false);
                }}
                showCloseButton={false}
                confirmTitle={`Purchase fee refund for the order ${orderDetails?.id}`}
                confirmDescription={`Refund the purchase fee with value of ${priceFormat(
                  {
                    currency: orderDetails?.currencyCode,
                    amount:
                      Number(orderDetails?.sumPurchaseFeePurchaseFeeVat) || 0,
                  }
                )} (VAT inclusive)`}
                confirmForm={() => {}}
                confirmButtons={() => {}}
                resultTitle={`Purchase fee refund for the order ${orderDetails?.id}`}
                resultDescriptionComponent={`Refund the purchase fee with value of ${priceFormat(
                  {
                    currency: orderDetails?.currencyCode,
                    amount:
                      Number(orderDetails?.sumPurchaseFeePurchaseFeeVat) || 0,
                  }
                )} (VAT inclusive)`}
                resultButtons={
                  <>
                    <Button
                      extraClass="btn-black secondary"
                      onClick={() => setPurchaseFeeRefundModal(false)}
                    >
                      Cancel
                    </Button>
                    <Button
                      extraClass="btn-black primary"
                      onClick={handleRefundPurchaseFee}
                    >
                      Confirm
                    </Button>
                  </>
                }
                showResult
                resultStatus="none"
              />
            </Box>
            <Content
              orderId={params.orderId}
              product={product}
              nextPayment={nextPayment}
              paidAmount={paidAmount}
            />

            {!!chargebacks.length && (
              <div className="list-transactions">
                <ChargebackList ns={ns} chargebacks={chargebacks} t={t} />
              </div>
            )}

            {customFieldsKeys?.length > 0 ? (
              <div className={css.customFields}>
                <h3 className="!text-[18px] font-bold">{t('order.customFields')}</h3>
                <Divider />
                <Box withHeader className={`${ns}__box ${css.box}`}>
                  <div className={css.line}>
                    {customFieldsKeys.map((key: any) => (
                      <p key={key} className="d-flex justify-content-between">
                        <span className={css.field}>{key}</span>
                        <span className={css.field}>{customFields[key]}</span>
                      </p>
                    ))}
                  </div>
                </Box>
              </div>
            ) : null}

            <Timeline
              installments={instalments}
              timelineDetails={timelineDetails}
              loading={isTimelineLoading}
              type={productType}
              isPayNow={false}
              currency={orderDetails.currencyCode}
            />
          </>
        )}
      </PageWithNavigation>
    </div>
  );
};

export default OrderDetail;
